"use client"

import { motion, AnimatePresence } from "framer-motion"
import { X, ExternalLink, Github, ChevronLeft, ChevronRight } from "lucide-react"
import Image from "next/image"
import { useState } from "react"

interface ProjectModalProps {
    project: {
        title: string
        description: string
        image: string
        tags: string[]
        links: { demo: string; github: string }
        color: string
        caseStudy?: {
            problem: string
            solution: string
            outcome: string
            screenshots?: string[]
        }
    } | null
    isOpen: boolean
    onClose: () => void
}

export function ProjectModal({ project, isOpen, onClose }: ProjectModalProps) {
    const [currentScreenshot, setCurrentScreenshot] = useState(0)

    if (!project) return null

    const screenshots = project.caseStudy?.screenshots || [project.image]

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50"
                    />

                    {/* Modal */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        className="fixed inset-4 md:inset-[10%] bg-card border border-border rounded-3xl overflow-hidden z-50 flex flex-col"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between p-6 border-b border-border">
                            <div>
                                <h2 className="text-2xl font-bold">{project.title}</h2>
                                <p className="text-muted-foreground">{project.description}</p>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 rounded-full hover:bg-muted/20 transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-6">
                            <div className="grid lg:grid-cols-2 gap-8">
                                {/* Screenshots */}
                                <div className="space-y-4">
                                    <div className="relative aspect-video rounded-xl overflow-hidden bg-muted">
                                        <div className={`absolute inset-0 bg-gradient-to-br ${project.color} opacity-30`} />
                                        <Image
                                            src={screenshots[currentScreenshot]}
                                            alt={project.title}
                                            fill
                                            className="object-cover"
                                        />

                                        {screenshots.length > 1 && (
                                            <>
                                                <button
                                                    onClick={() => setCurrentScreenshot((prev) => (prev - 1 + screenshots.length) % screenshots.length)}
                                                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-background/80 hover:bg-background transition-colors"
                                                >
                                                    <ChevronLeft className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => setCurrentScreenshot((prev) => (prev + 1) % screenshots.length)}
                                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-background/80 hover:bg-background transition-colors"
                                                >
                                                    <ChevronRight className="w-5 h-5" />
                                                </button>
                                            </>
                                        )}
                                    </div>

                                    {screenshots.length > 1 && (
                                        <div className="flex gap-2 justify-center">
                                            {screenshots.map((_, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => setCurrentScreenshot(index)}
                                                    className={`w-2 h-2 rounded-full transition-all ${index === currentScreenshot ? "w-6 bg-primary" : "bg-muted-foreground/30"
                                                        }`}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Details */}
                                <div className="space-y-6">
                                    {/* Tech stack */}
                                    <div>
                                        <h3 className="text-lg font-semibold mb-3">Tech Stack</h3>
                                        <div className="flex flex-wrap gap-2">
                                            {project.tags.map((tag) => (
                                                <span
                                                    key={tag}
                                                    className="px-3 py-1 rounded-full text-sm bg-primary/10 border border-primary/20 text-primary"
                                                >
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    {project.caseStudy && (
                                        <>
                                            {/* Problem */}
                                            <div>
                                                <h3 className="text-lg font-semibold mb-2">The Challenge</h3>
                                                <p className="text-muted-foreground">{project.caseStudy.problem}</p>
                                            </div>

                                            {/* Solution */}
                                            <div>
                                                <h3 className="text-lg font-semibold mb-2">The Solution</h3>
                                                <p className="text-muted-foreground">{project.caseStudy.solution}</p>
                                            </div>

                                            {/* Outcome */}
                                            <div>
                                                <h3 className="text-lg font-semibold mb-2">The Outcome</h3>
                                                <p className="text-muted-foreground">{project.caseStudy.outcome}</p>
                                            </div>
                                        </>
                                    )}

                                    {/* Links */}
                                    <div className="flex gap-4 pt-4">
                                        {project.links.demo && (
                                            <a
                                                href={project.links.demo}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-full font-medium hover:bg-primary/90 transition-colors"
                                            >
                                                <ExternalLink className="w-4 h-4" />
                                                Live Demo
                                            </a>
                                        )}
                                        {project.links.github && project.links.github !== "#" && (
                                            <a
                                                href={project.links.github}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-2 px-6 py-3 border border-border rounded-full font-medium hover:bg-muted/20 transition-colors"
                                            >
                                                <Github className="w-4 h-4" />
                                                Source Code
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    )
}
